import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import AITestingAgent from "./pages/AITestingAgent";
import CreateAgent from "./pages/CreateAgent";
// VoicePersona page removed — personas live under /live-simulations/scenario (Persona tab)
import Settings from "./components/settings/Settings";
import ClientSettings from "./components/ClientSettings";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useFeature } from "./hooks/useFeature";
import useEntitlements from "./hooks/useEntitlements";
import UpgradeGate from "./components/billing/UpgradeGate";
import { AgentProvider } from "./contexts/AgentContext";
import { PageTitleProvider } from "./contexts/PageTitleContext";
import CustomMetrics from "./pages/CustomMetrics";
import { ToastProvider } from "./contexts/ToastContext";
import { ScenarioGenerationTrackerProvider } from "./contexts/ScenarioGenerationTrackerContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import Scenario from "./pages/live-simulations/Scenario";
import CreateScenario from "./pages/CreateScenario";
import GenerateScenarios from "./pages/live-simulations/GenerateScenarios";
import CreatePersona from "./pages/CreatePersona";
import BatchRun from "./pages/BatchRun";
import NewBatchRun from "./pages/NewBatchRun";
import BatchRunDetails from "./pages/BatchRunDetails";
import StressTestResults from "./pages/stress-test/StressTestResults";
import StressTestSetup from "./pages/stress-test/StressTestSetup";
import { RunResults } from "./pages/batch";
import Metrics from "./pages/Metrics";
import MetricsGallery from "./pages/MetricsGallery";
import MetricsGalleryPreview from "./pages/MetricsGalleryPreview";
import MetricOptimization from "./pages/MetricOptimization";
import GenerateMetrics from "./pages/GenerateMetrics";
import PromptOptimizer from "./pages/PromptOptimizer";
import NewPromptOptimization from "./pages/NewPromptOptimization";
import PromptOptimizationDiffViewer from "./pages/PromptOptimizationDiffViewer";
import PromptOptimizationStatus from "./pages/PromptOptimizationStatus";
import Conversations from "./pages/observability/Conversations";
import ConversationDetail from "./pages/observability/ConversationDetail";
import Alerts from "./pages/observability/Alerts";
import Discovery from "./pages/observability/Discovery";
import Analytics from "./pages/Analytics";
import Layout from "./components/layout/Layout";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import AcceptInvite from "./components/auth/AcceptInvite";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminMetricsGallery from "./pages/admin/AdminMetricsGallery";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminPlanFeatureAssignments from "./pages/admin/AdminPlanFeatureAssignments";
import AdminStripeEvents from "./pages/admin/AdminStripeEvents";
import AdminPhoneNumbers from "./pages/admin/AdminPhoneNumbers";
import AdminFeatureAccess from "./pages/admin/AdminFeatureAccess";
import AdminRolePermissions from "./pages/admin/AdminRolePermissions";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminDispatchBreakers from "./pages/admin/AdminDispatchBreakers";
import AdminRateLimits from "./pages/admin/AdminRateLimits";
import AdminCallLimits from "./pages/admin/AdminCallLimits";
import AdminTurnTaking from "./pages/admin/AdminTurnTaking";
import AdminStressAdmission from "./pages/admin/AdminStressAdmission";
import Billing from "./pages/Billing";
import SeatLockGate from "./components/billing/SeatLockGate";
import ArchivedAccountGate from "./components/billing/ArchivedAccountGate";
import WorkspaceArchivedListener from "./components/billing/WorkspaceArchivedListener";
import NoWorkspaceScreen from "./components/workspace/NoWorkspaceScreen";
import WorkspacesLoadError from "./components/workspace/WorkspacesLoadError";
import { WorkspaceGate, WorkspacelessSettingsRedirect, RootRedirect } from "./components/workspace/workspaceRoutes";

// Component to handle token extraction from URL
const TokenHandler = ({ children }) => {
  const { setToken, setUser, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Effect for initial token check
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUserData = localStorage.getItem("userData");

    if (storedToken) {
      setToken(storedToken);
      if (storedUserData) {
        setUser(JSON.parse(storedUserData));
      }
    }
  }, []); // Run only once on mount

  // Effect for handling URL token
  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    // The invite link is /accept-invite?token=<invitationToken> — that `token`
    // is an INVITATION token, not an auth JWT. Consuming it here set it as the
    // auth token and stripped the query string (navigate(pathname)), which
    // bounced the invitee to /login with no token. Leave it for AcceptInvite,
    // which reads the param itself. (OAuth redirects land on /dashboard, never
    // here, so this never affects sign-in.)
    if (location.pathname === "/accept-invite") {
      return;
    }

    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const encodedUserData = params.get("user_data");

    if (token) {
      const cleanToken = token.startsWith("Bearer ")
        ? token.substring(7)
        : token;
      setToken(cleanToken);

      let userData;

      if (encodedUserData) {
        try {
          userData = JSON.parse(atob(encodedUserData));
        } catch (error) {
          console.error("Error decoding user data from URL:", error);
        }
      }

      if (!userData) {
        try {
          const base64Url = cleanToken.split(".")[1];
          const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split("")
              .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join("")
          );

          const tokenData = JSON.parse(jsonPayload);
          userData = {
            id: tokenData.sub,
            email: tokenData.email,
            name: tokenData.name,
            client_id: tokenData.client_id,
          };
        } catch (error) {
          console.error("Error decoding token:", error);
        }
      }

      if (userData) {
        setUser(userData);
        localStorage.setItem("userData", JSON.stringify(userData));
      }

      // Clear the URL parameters
      navigate(location.pathname, { replace: true });
    }
  }, [location, isAuthenticated, setToken, setUser, navigate]);

  return children;
};

// Shared authenticated layout route — mounts once, persists across navigation
const PrivateLayoutRoute = () => {
  const { isAuthenticated, isInitialized, workspaces, workspacesLoaded, workspacesError } = useAuth();
  const location = useLocation();

  // Wait for auth to initialize from localStorage before redirecting
  if (!isInitialized) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // Onboarding is automatic (trial seeded + user marked onboarded at signup),
  // so there's no first-run plan-picker gate — users land on the dashboard.

  // A failed GET /workspaces must NOT be read as "confirmed zero
  // workspaces" (see AuthContext.fetchWorkspaces) — that would show owners a
  // duplicate "create your first workspace" form and wrongly tell members
  // they have no access, when the real problem is just a failed request.
  if (workspacesError) {
    return <WorkspacesLoadError />;
  }

  // A signed-in user with zero live workspaces (all archived, or every
  // membership removed) gets a full-page screen instead of the sidebar shell.
  // This must happen BEFORE <Layout /> renders — Layout always shows the
  // sidebar, and WorkspaceGate (nested inside it for /:clientUuid routes)
  // would otherwise render the no-workspace screen inside that sidebar shell.
  // Checking here also covers the non-UUID routes Layout wraps directly
  // (e.g. /settings/billing).
  if (workspacesLoaded && workspaces.length === 0) {
    return <NoWorkspaceScreen />;
  }

  return <Layout />;
};

// Workspace-aware redirect for in-tree index/legacy routes (prefixes the UUID).
const WsRedirect = ({ to }) => {
  const { clientUuid } = useParams();
  return <Navigate to={`/${clientUuid}/${to.replace(/^\//, "")}`} replace />;
};

// Route guard for plan-gated pages. Renders the page only when the workspace's
// plan includes `feature` (internal admins always pass, via useFeature). The nav
// already hides gated items, but this stops direct-URL navigation from landing
// on a page whose API now 403s. We wait on accessLoaded so an entitled user
// isn't bounced during the /me/access load window on a hard refresh.
//
// A disabled feature has two different causes, and they need different UX:
//   - the workspace is pay-as-you-go and the feature is Growth-only  -> this
//     is a monetization opportunity, show the inline upsell (UpgradeGate).
//   - the workspace is on Growth/Enterprise but an admin turned this specific
//     feature off via a FeatureOverride -> that's a legitimate admin-config
//     case, not something "upgrade" fixes, so keep the original silent
//     redirect to the dashboard.
// `useEntitlements()` starts `loading: true` on every fresh mount (its own
// async fetch, independent of `accessLoaded`), so on normal in-app nav to a
// gated route this component mounts with entLoading still true. We must wait
// for it before choosing UpgradeGate vs Navigate — deciding while it's still
// loading would race the fetch and always fall through to Navigate, since
// isPayg's default is unknown/false until the fetch resolves.
const FeatureRoute = ({ feature, featureLabel, children }) => {
  const { accessLoaded } = useAuth();
  const { clientUuid } = useParams();
  const enabled = useFeature(feature);
  const { isPayg, loading: entLoading } = useEntitlements();
  if (!accessLoaded) return null;
  if (enabled) return children;
  if (entLoading) return null; // wait for entitlements before deciding upsell vs redirect
  if (isPayg) return <UpgradeGate featureLabel={featureLabel || "This"} />;
  return <Navigate to={`/${clientUuid}/dashboard`} replace />;
};

// WorkspaceGate, WorkspacelessSettingsRedirect and RootRedirect live in
// ./components/workspace/workspaceRoutes so they can be unit-tested without
// importing this whole file (and every page component it pulls in).

const App = () => {
  return (
    <AuthProvider>
      {/* Mounted once, above everything else route-related, so a
          seat_limit_exceeded 403 (a Growth workspace downgraded to fewer
          seats than it has members — see src/utils/auth.js) replaces the
          whole app with a full-screen explanation regardless of which page
          triggered it. The owner is never seat-locked, so this never
          activates for them. */}
      {/* Same mechanism, for an account archived mid-session: the 410 can
          land on any page, so the explanation is mounted once at the top
          rather than handled per-route. */}
      <ArchivedAccountGate>
      <SeatLockGate>
      <ToastProvider>
        {/* A single workspace (not the whole account) archived mid-session:
            toast + move the user to another live workspace, or "/" (renders
            the no-workspace screen) — unlike account_archived above, there is
            somewhere to go, so no full-screen gate. Needs AuthProvider
            (fetchWorkspaces), ToastProvider (showToast) and the Router
            (useNavigate) above/around it. */}
        <WorkspaceArchivedListener />
        <AgentProvider>
          <PageTitleProvider>
           <ScenarioGenerationTrackerProvider>
            <OnboardingProvider>
              <TokenHandler>
                <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />

                {/* Account-level pages: authenticated + persistent Layout, but
                    NOT workspace-scoped (no UUID). Billing spans workspaces. */}
                <Route element={<PrivateLayoutRoute />}>
                  <Route path="/settings/billing" element={<Billing />} />
                </Route>

                {/* Workspace-less /settings/* — external OAuth callbacks (e.g.
                    Slack) return to a static redirect_uri with no UUID. Redirect
                    into the active workspace, preserving the query so callback
                    params survive. Static /settings/billing above outranks this
                    dynamic :section, so it is unaffected. */}
                <Route path="/settings/:section" element={<WorkspacelessSettingsRedirect />} />

                {/* All workspace pages live under /:clientUuid and share a single
                    persistent Layout. WorkspaceGate keeps the active workspace in
                    sync with the UUID and rejects unknown UUIDs. */}
                <Route path="/:clientUuid" element={<PrivateLayoutRoute />}>
                  <Route element={<WorkspaceGate />}>
                    <Route index element={<WsRedirect to="dashboard" />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="onboarding" element={<Onboarding />} />
                    <Route path="ai-testing-agent" element={<AITestingAgent />} />
                    <Route path="ai-testing-agent/create" element={<CreateAgent key="create" />} />
                    <Route path="edit-agent/:id" element={<CreateAgent key="edit" />} />
                    <Route path="live-simulations" element={<WsRedirect to="live-simulations/scenario" />} />
                    <Route path="live-simulations/scenario" element={<Scenario />} />
                    <Route path="live-simulations/persona" element={<Scenario />} />
                    <Route path="live-simulations/attributes" element={<Scenario />} />
                    <Route path="live-simulations/schedules" element={<FeatureRoute feature="scheduled_runs.schedule_library" featureLabel="Scheduled runs"><Scenario /></FeatureRoute>} />
                    <Route path="live-simulations/schedules/new" element={<FeatureRoute feature="scheduled_runs.schedule_library" featureLabel="Scheduled runs"><Scenario /></FeatureRoute>} />
                    <Route path="live-simulations/schedules/:scheduleId/edit" element={<FeatureRoute feature="scheduled_runs.schedule_library" featureLabel="Scheduled runs"><Scenario /></FeatureRoute>} />
                    <Route path="live-simulations/scenarios/create" element={<CreateScenario />} />
                    <Route path="live-simulations/scenarios/:id/edit" element={<CreateScenario />} />
                    <Route path="live-simulations/generate" element={<GenerateScenarios />} />
                    <Route path="live-simulations/scenarios/:scenarioId/stress-test" element={<FeatureRoute feature="live_simulations.stress_testing" featureLabel="Stress testing"><StressTestSetup /></FeatureRoute>} />
                    {/* Legacy /voice-persona redirects to the persona tab under live simulations */}
                    <Route path="voice-persona" element={<WsRedirect to="live-simulations/persona" />} />
                    <Route path="voice-persona/create" element={<CreatePersona />} />
                    <Route path="voice-persona/edit/:id" element={<CreatePersona />} />
                    <Route path="observability/conversations" element={<Conversations />} />
                    <Route path="observability/notifications" element={<FeatureRoute feature="observability.alerts" featureLabel="Alert monitors"><Alerts /></FeatureRoute>} />
                    <Route path="observability/notifications/new" element={<FeatureRoute feature="observability.alerts" featureLabel="Alert monitors"><Alerts /></FeatureRoute>} />
                    <Route path="observability/notifications/:id/edit" element={<FeatureRoute feature="observability.alerts" featureLabel="Alert monitors"><Alerts /></FeatureRoute>} />
                    {/* Back-compat: earlier alerts paths now render the Notifications page */}
                    <Route path="observability/alerts" element={<FeatureRoute feature="observability.alerts" featureLabel="Alert monitors"><Alerts /></FeatureRoute>} />
                    <Route path="observability/conversations/alerts" element={<FeatureRoute feature="observability.alerts" featureLabel="Alert monitors"><Alerts /></FeatureRoute>} />
                    <Route path="observability/conversations/:id" element={<ConversationDetail />} />
                    <Route path="copilot" element={<FeatureRoute feature="observability.discovery" featureLabel="Co-Pilot"><Discovery /></FeatureRoute>} />
                    <Route path="copilot/:conversationId" element={<FeatureRoute feature="observability.discovery" featureLabel="Co-Pilot"><Discovery /></FeatureRoute>} />
                    <Route path="observability/discovery" element={<WsRedirect to="copilot" />} />
                    <Route path="analytics" element={<Analytics />} />
                    {/* Each settings nav is its own page: /settings/<section>.
                        (Billing is account-level and routed outside the UUID.) */}
                    <Route path="settings" element={<WsRedirect to="settings/workspace" />} />
                    <Route path="settings/billing" element={<Navigate to="/settings/billing" replace />} />
                    <Route path="settings/:section" element={<Settings />} />
                    <Route path="evaluation/custom-metrics" element={<CustomMetrics />} />
                    <Route path="evaluation/metrics" element={<Metrics />} />
                    <Route path="evaluation/metrics/gallery" element={<MetricsGallery />} />
                    <Route path="evaluation/metrics/gallery/:id" element={<MetricsGalleryPreview />} />
                    <Route path="evaluation/metrics/standard" element={<Metrics />} />
                    <Route path="evaluation/metrics/custom" element={<Metrics />} />
                    <Route path="evaluation/metrics/global" element={<Metrics />} />
                    <Route path="evaluation/metrics/optimization" element={<Metrics />} />
                    <Route path="evaluation/metrics/:metricId/optimization" element={<MetricOptimization />} />
                    <Route path="evaluation/generate-metrics" element={<GenerateMetrics />} />
                    <Route path="batch-run" element={<BatchRun />} />
                    <Route path="batch-run/new" element={<NewBatchRun />} />
                    <Route path="batch-run/:id" element={<BatchRunDetails />} />
                    <Route path="stress-test/:id" element={<StressTestResults />} />
                    <Route path="batch-run/:batchId/run/:runId" element={<RunResults />} />
                    <Route path="prompt-optimizer" element={<FeatureRoute feature="prompt_optimizer.optimization_runs" featureLabel="Prompt Optimizer"><PromptOptimizer /></FeatureRoute>} />
                    <Route path="prompt-optimizer/new" element={<FeatureRoute feature="prompt_optimizer.optimization_runs" featureLabel="Prompt Optimizer"><NewPromptOptimization /></FeatureRoute>} />
                    <Route path="prompt-optimizer/status/:id" element={<FeatureRoute feature="prompt_optimizer.optimization_runs" featureLabel="Prompt Optimizer"><PromptOptimizationStatus /></FeatureRoute>} />
                    <Route path="prompt-optimization-diff-viewer/:id" element={<FeatureRoute feature="prompt_optimizer.optimization_runs" featureLabel="Prompt Optimizer"><PromptOptimizationDiffViewer /></FeatureRoute>} />
                  </Route>
                </Route>

                {/* Admin Portal */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Navigate to="/admin/billing" replace />} />
                  <Route path="billing" element={<AdminBilling />} />
                  <Route path="pricing" element={<AdminPricing />} />
                  <Route path="metrics-gallery" element={<AdminMetricsGallery />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="plans" element={<AdminPlans />} />
                  <Route path="plans/:id/feature-assignments" element={<AdminPlanFeatureAssignments />} />
                  <Route path="phone-numbers" element={<AdminPhoneNumbers />} />
                  <Route path="stripe-events" element={<AdminStripeEvents />} />
                  <Route path="feature-access" element={<AdminFeatureAccess />} />
                  <Route path="role-permissions" element={<AdminRolePermissions />} />
                  <Route path="audit-log" element={<AdminAuditLog />} />
                  <Route path="dispatch-breakers" element={<AdminDispatchBreakers />} />
                  <Route path="rate-limits" element={<AdminRateLimits />} />
                  <Route path="call-limits" element={<AdminCallLimits />} />
                  <Route path="turn-taking" element={<AdminTurnTaking />} />
                  <Route path="stress-admission" element={<AdminStressAdmission />} />
                </Route>

                <Route path="/" element={<RootRedirect />} />
                <Route path="*" element={<RootRedirect />} />
              </Routes>
              </TokenHandler>
            </OnboardingProvider>
           </ScenarioGenerationTrackerProvider>
          </PageTitleProvider>
        </AgentProvider>
      </ToastProvider>
      </SeatLockGate>
      </ArchivedAccountGate>
    </AuthProvider>
  );
};

export default App;
